import { useState, useEffect, createContext, useContext, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// API Configuration
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

// Edit Mode Context - true when navigated from admin (?mode=admin)
const EditModeContext = createContext(false);
export const useEditMode = () => {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  return params.get('mode') === 'admin';
};

// Theme Context - dark/light mode
const ThemeContext = createContext({ theme: 'light', toggleTheme: () => {} });
export const useTheme = () => useContext(ThemeContext);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth helpers for token-based auth (localStorage fallback for cookies)
const getAuthHeaders = () => {
  const token = localStorage.getItem("session_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/** Clear any stale `session_token` cookie left over from a previous deploy.
 * Server rotates tokens but the browser keeps the old cookie for up to 7
 * days (max-age set by /api/auth/login). When the cookie no longer matches
 * a live session in the DB, every authFetch returns 401 — even though
 * localStorage may have a fresh Bearer token. Wiping it is harmless: the
 * Authorization: Bearer header is the canonical source of truth.
 */
const clearStaleSessionCookie = () => {
  try {
    const expire = "Thu, 01 Jan 1970 00:00:00 GMT";
    // Wipe on the current host AND parent domain (covers both
    // `pravaahbyacl.in` and `app.pravaahbyacl.in` / `*.preview.emergentagent.com`).
    document.cookie = `session_token=; expires=${expire}; path=/`;
    document.cookie = `session_token=; expires=${expire}; path=/; domain=${window.location.hostname}`;
    const parts = window.location.hostname.split(".");
    if (parts.length >= 2) {
      const parent = `.${parts.slice(-2).join(".")}`;
      document.cookie = `session_token=; expires=${expire}; path=/; domain=${parent}`;
    }
  } catch { /* ignore */ }
};

export const authFetch = async (url, options = {}) => {
  const headers = { ...getAuthHeaders(), ...(options.headers || {}) };
  const res = await fetch(url, { ...options, headers, credentials: "include" });
  // If we got a 401 on what should have been an authenticated call AND we DO
  // have a localStorage token, the most likely culprit is a stale cookie
  // shadowing the Bearer header server-side. Clear it so the next request
  // sends only the Bearer token.
  if (res.status === 401 && localStorage.getItem("session_token")) {
    clearStaleSessionCookie();
  }
  return res;
};

// Auth Provider
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Skip the /auth/me probe entirely when no session token is stored —
    // avoids the cosmetic 401 console noise on the first paint and on any
    // unauthenticated page (e.g. /login, /apply).
    const stored = localStorage.getItem("session_token");
    if (!stored) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const response = await authFetch(`${API}/auth/me`);

      if (response.ok) {
        const data = await response.json();
        setUser(data.data);
      } else {
        localStorage.removeItem("session_token");
        setUser(null);
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (userData, token) => {
    if (token) {
      // Wipe any pre-existing cookie BEFORE storing the new token. On
      // production this protects against a stale `session_token` cookie
      // (left over from an earlier deploy whose DB session is gone)
      // shadowing the Bearer header on subsequent requests.
      try {
        const expire = "Thu, 01 Jan 1970 00:00:00 GMT";
        document.cookie = `session_token=; expires=${expire}; path=/`;
        document.cookie = `session_token=; expires=${expire}; path=/; domain=${window.location.hostname}`;
        const parts = window.location.hostname.split(".");
        if (parts.length >= 2) {
          document.cookie = `session_token=; expires=${expire}; path=/; domain=.${parts.slice(-2).join(".")}`;
        }
      } catch { /* ignore */ }
      localStorage.setItem("session_token", token);
    }
    setUser(userData);
  };

  const logout = async () => {
    try {
      await authFetch(`${API}/auth/logout`, { method: "POST" });
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem("session_token");
    setUser(null);
  };

  // Listen for force_logout pushes (admin deactivated/deleted this user from another tab/device).
  // Backend emits sio.emit('force_logout', {reason}, room=f'user_{uid}') in
  // routes/auth.py:admin_delete_user + admin_hard_delete_user + employees.delete_employee.
  useEffect(() => {
    if (!user?.user_id) return undefined;
    let socket;
    let cancelled = false;
    (async () => {
      const mod = await import("@/hooks/useSocketIo");
      socket = mod.getSocketIo?.();
      if (!socket || cancelled) return;
      const handler = (msg) => {
        const reason = msg?.reason || "account_changed";
        localStorage.removeItem("session_token");
        setUser(null);
        // surface a clear message before the redirect kicks in
        try {
          const txt = reason === "account_deleted" ? "Your account was deleted by an admin." :
                      reason === "employee_deactivated" ? "Your employee record was deactivated." :
                      "Your account was deactivated.";
          import("sonner").then(({ toast }) => toast.error("Logged out", { description: txt, duration: 7000 }));
        } catch { /* noop */ }
      };
      socket.on("force_logout", handler);
      // cleanup
      return () => socket?.off("force_logout", handler);
    })();
    return () => { cancelled = true; };
  }, [user?.user_id]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login", { state: { from: location }, replace: true });
    }
  }, [user, loading, navigate, location]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 spinner mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return user ? children : null;
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      // Clear hash fragment immediately to prevent re-triggering on refresh
      if (window.history.replaceState) {
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      }
      
      if (!sessionIdMatch) {
        navigate("/login", { replace: true });
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        const response = await authFetch(`${API}/auth/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId }),
        });

        if (response.ok) {
          const data = await response.json();
          login(data.data, data.token);
          
          // Seed demo data
          await authFetch(`${API}/seed`, { method: "POST" });
          
          toast.success("Welcome to Pravaah!");
          navigate("/dashboard", { replace: true, state: { user: data.data } });
        } else {
          const errData = await response.json().catch(() => ({}));
          const msg = errData.detail || "Google sign-in session expired. Please try again.";
          toast.error(msg);
          navigate("/login", { replace: true });
        }
      } catch (error) {
        console.error("Auth callback error:", error);
        toast.error("Connection error during sign-in. Please try again.");
        navigate("/login", { replace: true });
      }
    };

    processAuth();
  }, [navigate, login]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="w-12 h-12 spinner mx-auto mb-4"></div>
        <p className="text-slate-600">Authenticating...</p>
      </div>
    </div>
  );
};

// Lazy load pages
import LoginPage from "@/pages/LoginPage";
import MainLayout from "@/components/layout/MainLayout";
import Dashboard from "@/pages/Dashboard";
import EmployeesPage from "@/pages/EmployeesPage";
import AttendancePage from "@/pages/AttendancePage";
import DealersPage from "@/pages/DealersPage";
import OnboardingPage from "@/pages/OnboardingPage";
import ApprovalsPage from "@/pages/ApprovalsPage";
import PublicBusinessAssociateForm from "@/pages/PublicBusinessAssociateForm";
import ProductsPage from "@/pages/ProductsPage";
import OrdersPage from "@/pages/OrdersPage";
import CollectionsPage from "@/pages/CollectionsPage";
import SchemesPage from "@/pages/SchemesPage";
import TasksPage from "@/pages/TasksPage";
import ExpensesPage from "@/pages/ExpensesPage";
import BEATsPage from "@/pages/BEATsPage";
import NotificationsPage from "@/pages/NotificationsPage";
import UserManagementPage from "@/pages/UserManagementPage";
import FieldForceDashboard from "@/pages/FieldForceDashboard";
import MyPerformancePage from "@/pages/MyPerformancePage";
import LiveTrackingPage from "@/pages/LiveTrackingPage";
import LeavePage from "@/pages/LeavePage";
import Employee360View from "@/pages/Employee360View";
import TargetsPage from "@/pages/TargetsPage";
import AuditTrailPage from "@/pages/AuditTrailPage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import DealerMapPage from "@/pages/DealerMapPage";
import SchemeClaimsPage from "@/pages/SchemeClaimsPage";
import FieldCheckInsPage from "@/pages/FieldCheckInsPage";
import PushNotificationsPage from "@/pages/PushNotificationsPage";
import SchemeAchievementsPage from "@/pages/SchemeAchievementsPage";
import ChatPage from "@/pages/ChatPage";
import WebhooksPage from "@/pages/WebhooksPage";
import ApiDocsPage from "@/pages/ApiDocsPage";
import GrievancePage from "@/pages/GrievancePage";
import LoyaltyPage from "@/pages/LoyaltyPage";
import ReportsPage from "@/pages/ReportsPage";
import FieldForceSettingsPage from "@/pages/FieldForceSettingsPage";
import SalesForecastPage from "@/pages/SalesForecastPage";
import BrochuresPage from "@/pages/BrochuresPage";
import GeofencingPage from "@/pages/GeofencingPage";
import AdminPage from "@/pages/AdminPage";
import AdminConfigPage from "@/pages/AdminConfigPage";
import InventoryPage from "@/pages/InventoryPage";
import AnalyticsDashboardPage from "@/pages/AnalyticsDashboardPage";
import CostPerBagPage from "@/pages/CostPerBagPage";
import SalesAnalyticsPage from "@/pages/SalesAnalyticsPage";
import ReportSchedulingPage from "@/pages/ReportSchedulingPage";
import FlowsHubPage from "@/pages/FlowsHubPage";
import FlowsPage from "@/pages/FlowsPage";
import FlowInsightsPage from "@/pages/FlowInsightsPage";
import FlowMarketplacePage from "@/pages/FlowMarketplacePage";
import CustomTriggersPage from "@/pages/CustomTriggersPage";
import MobileIntegrationDocsPage from "@/pages/MobileIntegrationDocsPage";
import FieldTrackingMapPage from "@/pages/FieldTrackingMapPage";

// Factory Pages
import FactoryLayout from "@/components/layout/FactoryLayout";
import FactoryDashboard from "@/pages/factory/FactoryDashboard";
import ProductionLogPage from "@/pages/factory/ProductionLogPage";
import RawMaterialsPage from "@/pages/factory/RawMaterialsPage";
import FinishedGoodsPage from "@/pages/factory/FinishedGoodsPage";
import DispatchQueuePage from "@/pages/factory/DispatchQueuePage";
import QualityControlPage from "@/pages/factory/QualityControlPage";
import EquipmentPage from "@/pages/factory/EquipmentPage";
import SafetyPage from "@/pages/factory/SafetyPage";
import EnergyPage from "@/pages/factory/EnergyPage";
import MaterialEntryLogPage from "@/pages/factory/MaterialEntryLogPage";
import DailyReportsPage from "@/pages/factory/DailyReportsPage";
import FactoryExpensesPage from "@/pages/factory/FactoryExpensesPage";
import ManpowerPage from "@/pages/factory/ManpowerPage";
import ForecastPage from "@/pages/factory/ForecastPage";
import FactoryReportsPage from "@/pages/factory/ReportsPage";
import FactoryTasksPage from "@/pages/factory/FactoryTasksPage";
import FactoryChatPage from "@/pages/factory/FactoryChatPage";

// Alias factory reports to avoid conflict


// Role-based redirect component
const RoleRedirect = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "FACTORY_ADMIN") return <Navigate to="/factory" replace />;
  return <Navigate to="/dashboard" replace />;
};

// App Router Component
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id - must happen synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/apply" element={<PublicBusinessAssociateForm />} />
      <Route path="/business-associate-form" element={<PublicBusinessAssociateForm />} />
      {/* Factory Admin Panel */}
      <Route
        path="/factory"
        element={
          <ProtectedRoute>
            <FactoryLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<FactoryDashboard />} />
        <Route path="production" element={<ProductionLogPage />} />
        <Route path="raw-materials" element={<RawMaterialsPage />} />
        <Route path="finished-goods" element={<FinishedGoodsPage />} />
        <Route path="dispatch" element={<DispatchQueuePage />} />
        <Route path="qc" element={<QualityControlPage />} />
        <Route path="equipment" element={<EquipmentPage />} />
        <Route path="safety" element={<SafetyPage />} />
        <Route path="energy" element={<EnergyPage />} />
        <Route path="material-entries" element={<MaterialEntryLogPage />} />
        <Route path="daily-reports" element={<DailyReportsPage />} />
        <Route path="expenses" element={<FactoryExpensesPage />} />
        <Route path="manpower" element={<ManpowerPage />} />
        <Route path="forecast" element={<ForecastPage />} />
        <Route path="reports" element={<FactoryReportsPage />} />
        <Route path="tasks" element={<FactoryTasksPage />} />
        <Route path="chat" element={<FactoryChatPage />} />
      </Route>
      {/* Sales Admin Panel */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<RoleRedirect />} />
        <Route path="dashboard" element={<Dashboard />} />
        {/* Field Force */}
        <Route path="field-force/dashboard" element={<FieldForceDashboard />} />
        <Route path="my-performance" element={<MyPerformancePage />} />
        <Route path="field-force/tracking" element={<LiveTrackingPage />} />
        <Route path="field-force/360" element={<Employee360View />} />
        <Route path="field-force/beats" element={<BEATsPage />} />
        {/* Human Resource */}
        <Route path="hr/attendance" element={<AttendancePage />} />
        <Route path="hr/leave" element={<LeavePage />} />
        <Route path="leaves" element={<LeavePage />} />
        {/* Legacy redirects */}
        <Route path="attendance" element={<Navigate to="/hr/attendance" replace />} />
        <Route path="beats" element={<Navigate to="/field-force/beats" replace />} />
        <Route path="field-force/routes" element={<Navigate to="/field-force/beats" replace />} />
        <Route path="field-force/attendance" element={<Navigate to="/hr/attendance" replace />} />
        <Route path="field-force/leave" element={<Navigate to="/hr/leave" replace />} />
        {/* Sales & Operations */}
        <Route path="employees" element={<EmployeesPage />} />
        <Route path="dealers" element={<DealersPage />} />
        <Route path="onboarding" element={<OnboardingPage />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="collections" element={<CollectionsPage />} />
        <Route path="schemes" element={<SchemesPage />} />
        <Route path="scheme-claims" element={<SchemeClaimsPage />} />
        <Route path="field-checkins" element={<FieldCheckInsPage />} />
        <Route path="push-notifications" element={<PushNotificationsPage />} />
        <Route path="targets" element={<TargetsPage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="expenses" element={<ExpensesPage />} />
        <Route path="audit" element={<AuditTrailPage />} />
        <Route path="leaderboard" element={<LeaderboardPage />} />
        <Route path="approvals" element={<ApprovalsPage />} />
        <Route path="dealer-map" element={<DealerMapPage />} />
        <Route path="scheme-achievements" element={<SchemeAchievementsPage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="webhooks" element={<WebhooksPage />} />
        <Route path="api-docs" element={<ApiDocsPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="users" element={<UserManagementPage />} />
        <Route path="grievances" element={<GrievancePage />} />
        <Route path="loyalty" element={<LoyaltyPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="ff-settings" element={<FieldForceSettingsPage />} />
        <Route path="sales-forecast" element={<SalesForecastPage />} />
        <Route path="sales-analytics" element={<SalesAnalyticsPage />} />
        <Route path="report-scheduling" element={<ReportSchedulingPage />} />
        <Route path="flows" element={<FlowsHubPage />} />
        <Route path="flow-insights" element={<FlowsHubPage />} />
        <Route path="flow-marketplace" element={<FlowsHubPage />} />
        <Route path="flow-builder-legacy" element={<FlowsPage />} />
        <Route path="custom-triggers" element={<CustomTriggersPage />} />
        <Route path="mobile-integration-docs" element={<MobileIntegrationDocsPage />} />
        <Route path="brochures" element={<BrochuresPage />} />
        <Route path="geofencing" element={<GeofencingPage />} />
        <Route path="admin" element={<AdminPage />} />
        <Route path="admin/config" element={<AdminConfigPage />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="analytics" element={<AnalyticsDashboardPage />} />
        <Route path="analytics/cost-per-bag" element={<CostPerBagPage />} />
        <Route path="field-tracking" element={<FieldTrackingMapPage />} />
      </Route>
      <Route path="*" element={<RoleRedirect />} />
    </Routes>
  );
}

function App() {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('pravaah-theme') || 'light';
    }
    return 'light';
  });

  const toggleTheme = useCallback(() => {
    setTheme(prev => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('pravaah-theme', next);
      return next;
    });
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  return (
    <BrowserRouter>
      <ThemeContext.Provider value={{ theme, toggleTheme }}>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </ThemeContext.Provider>
    </BrowserRouter>
  );
}

export default App;
export { API };
